
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface SupportTicket {
  name: string;
  email: string;
  subject: string;
  description: string;
  status?: number;
  priority?: number;
}

export const freshdeskApiClient = {
  createTicket: async (ticket: SupportTicket) => {
    try {
      logger.info({
        message: "Creating support ticket",
        context: { subject: ticket.subject }
      });
      
      // Add validation before sending the request
      if (!ticket.email || !ticket.subject || !ticket.description) {
        throw new Error("Missing required fields for support ticket");
      }
      
      // Log the request
      logger.info({
        message: "Invoking Freshdesk Edge Function",
        context: { 
          action: 'createTicket',
          ticket: {
            name: ticket.name || 'Anonymous',
            email: ticket.email,
            subject: ticket.subject
          }
        }
      });
      
      // Try to call the Edge Function with retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let lastError = null;
      
      while (attempts < maxAttempts) {
        try {
          // Call the Edge Function
          const { data, error } = await supabase.functions.invoke('freshdesk-api', {
            body: {
              action: 'createTicket',
              ticket: {
                ...ticket,
                // Ensure all fields are strings to prevent serialization issues
                name: String(ticket.name || ''),
                email: String(ticket.email),
                subject: String(ticket.subject),
                description: String(ticket.description),
                // Default values for priority and status if not provided
                priority: ticket.priority || 1,
                status: ticket.status || 2
              }
            }
          });

          if (error) {
            logger.error({
              message: "Error from Edge Function when creating support ticket",
              error,
              context: { subject: ticket.subject, error: JSON.stringify(error) }
            });
            
            // Save error for potential retry
            lastError = error;
            attempts++;
            
            // Wait before retry
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
              continue;
            }
            
            throw new Error(`Failed to create support ticket: ${error.message}`);
          }
          
          logger.info({
            message: "Support ticket created successfully",
            context: { 
              ticketId: data?.id, 
              subject: ticket.subject,
              mock: data?._mock ? "Using mock response" : undefined
            }
          });
          
          return data;
        } catch (innerError) {
          // Handle fetch errors
          logger.error({
            message: "Network error during Edge Function call",
            error: innerError instanceof Error ? innerError : new Error(String(innerError)),
            context: { attempt: attempts + 1, maxAttempts }
          });
          
          lastError = innerError;
          attempts++;
          
          // Wait before retry
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
            continue;
          }
          
          // All retries failed
          throw innerError;
        }
      }
      
      // If we get here, all retries failed
      throw lastError || new Error("Maximum retry attempts reached");
    } catch (error) {
      logger.error({
        message: "Error creating support ticket",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { ticket }
      });
      
      throw error;
    }
  }
};
